import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import Avatar from '../epics/Avatar';
import JiraKeyChip from '../JiraKeyChip';
import { typeBadge } from '../hygiene/types';
import InfoTip from '../InfoTip';
import FilterChip from './FilterChip';
import StatusPill from './StatusPill';
import { StuckRow, WaitingData, WaitingStatus } from './types';
import * as fmt from '../../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const PAGE = 50;

// ─── Time in each status ────────────────────────────────────────────────────────

function TimeInStatus({ statuses }: { statuses: WaitingStatus[] }) {
  const maxMedian = Math.max(1, ...statuses.map((s) => s.median_hours ?? 0));
  return (
    <section className={`${CARD} p-5`} aria-label="Time in each status">
      <div className="flex items-center">
        <h3 className={CAPTION}>Time in each status</h3>
        <InfoTip id="flow.time-in-status" />
      </div>
      <ul className="mt-2 divide-y divide-gray-100">
        {statuses.map((s) => {
          const waits = s.stays - s.pass_through;
          // The bars use a square-root scale so a status that waits for days does not flatten the rest.
          const width = s.median_hours === null ? 0 : Math.max(2, (Math.sqrt(s.median_hours) / Math.sqrt(maxMedian)) * 100);
          return (
            <li key={s.status} className="grid gap-x-4 gap-y-1.5 py-3 sm:grid-cols-[9.5rem_minmax(0,1fr)_14rem] sm:items-center">
              <div>
                <StatusPill status={s.status} />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${width}%` }} />
                </div>
                <span className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900">{fmt.duration(s.median_hours)}</span>
              </div>
              <div className="text-xs leading-relaxed text-gray-500 sm:text-right">
                {s.p90_hours !== null && <div>Slowest 10% over {fmt.duration(s.p90_hours)}</div>}
                <div>
                  {waits} {waits === 1 ? 'wait' : 'waits'}
                  {s.pass_through > 0 ? ` · ${s.pass_through} passed through` : ''}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        Each bar is the typical wait (the median) for tickets that left the status this month. Stays under five minutes are tickets passing straight through, so they are counted but left out of the typical wait.
      </p>
    </section>
  );
}

// ─── Stuck now ──────────────────────────────────────────────────────────────────

interface StuckProps {
  data: WaitingData;
  projectKey: string | null;
  onProject: (key: string | null) => void;
  refreshKey: number;
}

function StuckList({ data, projectKey, onProject, refreshKey }: StuckProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [includeOlder, setIncludeOlder] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [rows, setRows] = useState<StuckRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getFlowStuck({
        project_key: projectKey ?? undefined,
        status: statusFilter ?? undefined,
        max_age_days: includeOlder ? undefined : 90,
        limit,
      })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setRows(r.data ?? []);
          setTotal(r.meta?.total ?? 0);
          setError(null);
        } else setError(r.error || 'Could not load the tickets that have not moved');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the tickets that have not moved'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectKey, statusFilter, includeOlder, limit, refreshKey]);

  const projects = data.stuck.by_project.slice(0, 10);
  const staleLabel = fmt.count(data.stuck.stale);

  return (
    <section className={`${CARD} overflow-hidden`} aria-label="Not moved for a while">
      <div className="p-5">
        <h3 className={CAPTION}>Tickets that have not moved</h3>
        <p className="mt-1 text-sm text-gray-600">
          Open tickets that have sat in a status longer than its limit. The limit is set per status in Settings.
        </p>

        {projects.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by project">
            <FilterChip label="All projects" selected={projectKey === null} onClick={() => onProject(null)} />
            {projects.map((p) => (
              <FilterChip
                key={p.project_key}
                label={p.project_key}
                title={p.project_name ?? undefined}
                count={includeOlder ? p.count : p.recent}
                selected={projectKey === p.project_key}
                onClick={() => onProject(projectKey === p.project_key ? null : p.project_key)}
              />
            ))}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          <FilterChip label="Any status" selected={statusFilter === null} onClick={() => { setStatusFilter(null); setLimit(PAGE); }} />
          {data.stuck.by_status.map((s) => (
            <FilterChip
              key={s.status}
              label={s.status}
              count={includeOlder ? s.count : s.count - s.over_90}
              title={`Limit ${s.threshold_days} days`}
              selected={statusFilter === s.status}
              onClick={() => { setStatusFilter(statusFilter === s.status ? null : s.status); setLimit(PAGE); }}
            />
          ))}
        </div>

        <label className="mt-4 flex items-start gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={includeOlder}
            onChange={(e) => { setIncludeOlder(e.target.checked); setLimit(PAGE); }}
            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-200"
          />
          <span>
            Include tickets that have not moved for more than 90 days ({staleLabel}).
            <span className="block text-xs text-gray-500">These are usually shipped but never closed in Jira, so they are hidden by default.</span>
          </span>
        </label>
      </div>

      {error && <div className="mx-5 mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">{error}</div>}

      {loading && rows.length === 0 ? (
        <div className="border-t border-gray-100" role="status" aria-label="Loading the tickets that have not moved">
          {[0, 1, 2].map((i) => (
            <div key={i} className="animate-pulse border-t border-gray-100 px-5 py-4 first:border-t-0 motion-reduce:animate-none">
              <div className="h-3 w-24 rounded bg-gray-100" />
              <div className="mt-2 h-4 w-2/3 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="border-t border-gray-100 px-5 py-12 text-center text-sm text-gray-500">
          {includeOlder ? 'Everything here has moved recently.' : 'Nothing has sat still for less than 90 days. Tick the box above to see older tickets.'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {rows.map((r) => (
            <li key={r.issue_key} className="flex items-start justify-between gap-4 px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <JiraKeyChip issueKey={r.issue_key} url={r.jira_url} />
                  {r.issue_type && <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(r.issue_type)}`}>{r.issue_type}</span>}
                  <span className="text-xs text-gray-500">{r.project_key}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={r.issue_name ?? undefined}>
                  {r.issue_name || 'Untitled'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <StatusPill status={r.status} />
                <span className="text-xs tabular-nums text-gray-500" title={`Limit ${r.threshold_days} days`}>
                  {fmt.count(r.days)} {r.days === 1 ? 'day' : 'days'}
                </span>
                {r.assignee && <Avatar name={r.assignee.name} size="sm" />}
              </div>
            </li>
          ))}
        </ul>
      )}

      {total > rows.length && (
        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={() => setLimit((n) => n + PAGE)}
            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] duration-150 hover:bg-blue-50/70 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Show {Math.min(PAGE, total - rows.length)} more <span className="text-gray-500">· {total - rows.length} left</span>
          </button>
        </div>
      )}
    </section>
  );
}

// ─── The Waiting tab ────────────────────────────────────────────────────────────

interface Props {
  period: string;
  projectKey: string | null;
  onProject: (key: string | null) => void;
  refreshKey: number;
}

export default function WaitingView({ period, projectKey, onProject, refreshKey }: Props) {
  const [data, setData] = useState<WaitingData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getFlowWaiting({ period, project_key: projectKey ?? undefined })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setData(r.data);
          setError(null);
        } else setError(r.error || 'Could not load waiting time');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load waiting time'));
    return () => {
      cancelled = true;
    };
  }, [period, projectKey, refreshKey]);

  // The slowest stage of the delivery path, judged on enough real waits to mean something.
  const slowest = useMemo(() => {
    const path = (data?.statuses ?? []).filter(
      (s) => s.position !== null && s.position >= 50 && s.position <= 100 && s.median_hours !== null && s.stays - s.pass_through >= 10
    );
    return path.sort((a, b) => (b.median_hours ?? 0) - (a.median_hours ?? 0))[0] ?? null;
  }, [data]);

  if (error) return <div className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">{error}</div>;
  if (!data) {
    return (
      <div className="space-y-5" role="status" aria-label="Loading waiting time">
        <div className="grid gap-5 sm:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className={`${CARD} h-28 animate-pulse motion-reduce:animate-none`} />)}
        </div>
        <div className={`${CARD} h-64 animate-pulse motion-reduce:animate-none`} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <section className={`${CARD} p-5`} aria-label="Not moved for a while">
          <div className="flex items-center">
            <h3 className={CAPTION}>Not moved for a while</h3>
            <InfoTip id="flow.stuck" />
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.count(data.stuck.recent)}</div>
          <p className="mt-1 text-sm text-gray-600">open tickets that have not moved for less than 90 days</p>
          {data.stuck.stale > 0 && (
            <p className="mt-2 text-xs leading-relaxed text-gray-500">
              {fmt.count(data.stuck.stale)} more have not moved for longer. They are usually shipped but never closed in Jira.
            </p>
          )}
        </section>
        <section className={`${CARD} p-5`} aria-label="Slowest stage">
          <div className="flex items-center">
            <h3 className={CAPTION}>Slowest stage this month</h3>
            <InfoTip id="flow.slowest" />
          </div>
          {slowest ? (
            <>
              <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.duration(slowest.median_hours)}</div>
              <p className="mt-1 text-sm text-gray-600">typical wait in {slowest.status}</p>
              <p className="mt-2 text-xs text-gray-500">The slowest 10% waited over {fmt.duration(slowest.p90_hours)}.</p>
            </>
          ) : (
            <p className="mt-2 text-sm text-gray-500">Not enough tickets left a delivery status this month to say.</p>
          )}
        </section>
      </div>

      {data.statuses.length > 0 ? (
        <TimeInStatus statuses={data.statuses} />
      ) : (
        <div className={`${CARD} px-5 py-10 text-center text-sm text-gray-500`}>No ticket changed status in this month.</div>
      )}

      <StuckList key={projectKey ?? 'all'} data={data} projectKey={projectKey} onProject={onProject} refreshKey={refreshKey} />
    </div>
  );
}
